import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useEffect } from 'react';

import api from 'apis/api';

import { Logger } from 'utils/logger';
import { getArtifactType } from 'utils/maxi-ai/artifactParser';
import { isObject, isString, isArray } from 'utils/type-guards/typeGuards';

import { useMaxiAIStore } from 'store/maxi-ai.store';

import type {
  ThreadsResponse,
  ThreadMessagesResponse,
  ThreadMessage,
  Message,
  ChatMessage,
  ThoughtProcess,
  Artifact,
} from 'types/maxi-ai';

// Helper function to parse artifact data from backend format
const parseArtifactData = (
  artifactData: unknown
): {
  type: string | null;
  content: unknown;
  status?: string;
  error?: string;
} => {
  if (!isObject(artifactData)) {
    return { type: null, content: null };
  }

  // Cast to Record for easier property access while maintaining type safety
  const data = artifactData;

  // Check for the specific artifact structure from the backend
  if (isString(data.artifact_type) && isString(data.artifact_content)) {
    const detectedType = getArtifactType(data.artifact_type);
    try {
      const parsedContent = JSON.parse(data.artifact_content);
      return { type: detectedType, content: parsedContent };
    } catch (e) {
      const errorMessage = `Failed to parse artifact content: ${e instanceof Error ? e.message : 'Invalid JSON'}`;
      Logger.error(errorMessage, { artifactData, error: e });

      // Return artifact with failed status and error details
      return {
        type: detectedType,
        content: null,
        status: 'failed',
        error: errorMessage,
      };
    }
  }

  // Check for explicit type field (legacy support)
  if (isString(data.type)) {
    const detectedType = getArtifactType(data.type);
    return { type: detectedType, content: data.data || data };
  }

  // Check if it looks like a table structure (has headers and rows)
  if ('headers' in data && 'rows' in data) {
    return { type: getArtifactType('table'), content: data };
  }

  // Check if it has raw data array (fallback to table)
  if ('data' in data && isArray(data.data)) {
    return { type: getArtifactType('table'), content: data.data };
  }

  // Check for chart-like structure
  if ('labels' in data || 'datasets' in data) {
    return { type: getArtifactType('line_chart'), content: data };
  }

  // If it's a string, it might be JSON-encoded artifact content
  if (typeof artifactData === 'string') {
    try {
      const parsedData = JSON.parse(artifactData);
      if (parsedData.headers && parsedData.rows) {
        return { type: getArtifactType('table'), content: parsedData };
      }
    } catch (e) {
      // Silently handle JSON parse errors
    }
  }

  // Fallback: default to table for any object data
  return { type: getArtifactType('table'), content: data };
};

// Parse thread messages into frontend Message format and extract artifacts
const parseThreadMessages = (
  threadMessages: ThreadMessage[]
): { messages: Message[]; artifacts: Record<string, Artifact> } => {
  const messages: Message[] = [];
  const artifacts: Record<string, Artifact> = {};

  threadMessages.forEach((threadMessage) => {
    const {
      message_type,
      content,
      artifacts: messageArtifacts,
    } = threadMessage;

    // Process artifacts if they exist - early return for performance
    if (!messageArtifacts) {
      // No artifacts to process, continue to next message
    } else {
      Object.entries(messageArtifacts).forEach(([artifactId, artifactData]) => {
        const {
          type: detectedType,
          content: parsedContent,
          status: parseStatus,
          error: parseError,
        } = parseArtifactData(artifactData);

        // Always create artifact entry, even with null content to preserve loading/failure states
        if (detectedType) {
          // Extract server-provided status and error information, with parser errors taking precedence
          const artifactStatus =
            parseStatus || (artifactData as any)?.artifact_status || 'complete';
          const artifactError =
            parseError || (artifactData as any)?.artifact_error;

          artifacts[artifactId] = {
            id: artifactId,
            type: detectedType as any, // Type assertion needed due to complex union type
            status: artifactStatus,
            content: parsedContent as any, // Type assertion needed - allow null content
            ...(artifactError && { error: artifactError }),
          };
        }
      });
    }

    if (message_type === 'USER' && content.type === 'user') {
      // Create user chat message
      const userMessage: ChatMessage = {
        type: 'CHAT_MESSAGE',
        id: `user-${dayjs().valueOf()}-${Math.random()}`,
        text: content.content, // Now guaranteed to be string due to discriminated union
        isUser: true,
        timestamp: dayjs().toDate(),
      };
      messages.push(userMessage);
    } else if (message_type === 'ASSISTANT' && content.type === 'assistant') {
      // Create thought process if available
      if (content.thought_process && content.thought_process.length > 0) {
        const thoughtProcess: ThoughtProcess = {
          type: 'THOUGHT_PROCESS',
          id: `thought-${dayjs().valueOf()}-${Math.random()}`,
          start: dayjs().valueOf(),
          end: dayjs().valueOf(),
          completeThoughts: content.thought_process.map((thought) => ({
            urls: [],
            thoughtText: thought.title,
            description: thought.description,
          })),
        };
        messages.push(thoughtProcess);
      }

      // Create assistant chat message
      const assistantMessage: ChatMessage = {
        type: 'CHAT_MESSAGE',
        id: `assistant-${dayjs().valueOf()}-${Math.random()}`,
        text: content.response, // Now guaranteed to be string due to discriminated union
        isUser: false,
        timestamp: dayjs().toDate(),
      };
      messages.push(assistantMessage);
    }
  });

  return { messages, artifacts };
};

export const useThreads = () => {
  // Get thread state from global store (shared across components)
  const {
    threads,
    threadId,
    setThreads,
    setMessages,
    setThreadId,
    setArtifact,
    clearArtifacts,
  } = useMaxiAIStore();

  // Fetch threads from API
  const fetchThreads = useCallback(async (): Promise<ThreadsResponse> => {
    const response = await api.get('/api/v2/maxi-ai/threads');
    return response.data;
  }, []);

  // Fetch messages for a specific thread
  const fetchThreadMessages = useCallback(
    async (threadId: string): Promise<ThreadMessagesResponse> => {
      const response = await api.get(`/api/v2/maxi-ai/threads/${threadId}`);
      return response.data;
    },
    []
  );

  // Select a thread and load its messages
  const selectThread = useCallback(
    async (threadId: string) => {
      try {
        // Set the thread ID immediately
        setThreadId(threadId);

        // Clear existing artifacts when switching threads
        clearArtifacts();

        // Fetch and parse the thread messages
        const threadMessagesResponse = await fetchThreadMessages(threadId);
        const { messages: parsedMessages, artifacts } = parseThreadMessages(
          threadMessagesResponse.messages
        );

        // Drop stale responses if selection changed mid-flight
        if (useMaxiAIStore.getState().threadId !== threadId) {
          return;
        }

        // Load artifacts into the store
        Object.entries(artifacts).forEach(([artifactId, artifact]) => {
          setArtifact(artifactId, artifact);
        });

        // Update the messages in the store
        setMessages(parsedMessages);
      } catch (error) {
        Logger.error('Error loading thread messages:', error);
        // Reset thread ID on error
        setThreadId(null);
        setMessages([]); // Clear messages to maintain consistent state
        clearArtifacts(); // Clear artifacts on error
        throw error;
      }
    },
    [fetchThreadMessages, setThreadId, setMessages, setArtifact, clearArtifacts]
  );

  // React Query for threads
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['maxi-ai-threads'],
    queryFn: fetchThreads,
  });

  // Update store when data changes
  useEffect(() => {
    if (data) {
      setThreads(data.threads);
    }
  }, [data, setThreads]);

  const updateThreadTitle = useCallback(
    async (threadId: string, newTitle: string) => {
      try {
        const { data } = await api.patch(
          `/api/v2/maxi-ai/threads/${threadId}`,
          {
            title: newTitle,
          }
        );

        // Update the thread in the local store
        const updatedThreads = threads.map((thread) =>
          thread.id === threadId ? { ...thread, title: newTitle } : thread
        );
        setThreads(updatedThreads);

        return data;
      } catch (error) {
        Logger.error('Error updating thread title:', error);
        throw error;
      }
    },
    [threads, setThreads]
  );

  const updateThreadPinned = useCallback(
    async (threadId: string, pinned: boolean) => {
      try {
        const { data } = await api.patch(
          `/api/v2/maxi-ai/threads/${threadId}`,
          { pinned }
        );

        const updatedThreads = threads.map((thread) =>
          thread.id === threadId ? { ...thread, pinned } : thread
        );
        setThreads(updatedThreads);

        return data;
      } catch (error) {
        Logger.error('Error updating thread pinned status:', error);
        throw error;
      }
    },
    [threads, setThreads]
  );

  const deleteThread = useCallback(
    async (threadIdToDelete: string) => {
      try {
        await api.delete(`/api/v2/maxi-ai/threads/${threadIdToDelete}`);

        // Remove the thread from the local store
        const updatedThreads = threads.filter(
          (thread) => thread.id !== threadIdToDelete
        );
        setThreads(updatedThreads);

        // If the deleted thread was the current thread, clear it
        if (threadId === threadIdToDelete) {
          setThreadId(null);
          setMessages([]);
        }
      } catch (error) {
        Logger.error('Error deleting thread:', error);
        throw error;
      }
    },
    [threads, threadId, setThreads, setThreadId, setMessages]
  );

  // Create a new thread (for new conversations)
  const createNewThread = useCallback(() => {
    setThreadId(null);
    setMessages([]);
  }, [setThreadId, setMessages]);

  return {
    // State
    threads,
    threadId,
    currentThread:
      threadId && threads ? threads.find((t) => t.id === threadId) : null,

    // Loading states
    isLoading,
    error,

    // Operations
    refetch,
    fetchThreads,
    selectThread,
    updateThreadTitle,
    updateThreadPinned,
    deleteThread,
    createNewThread,
  };
};
